import { useState } from "react";
import api, { useList, errMsg, fmtRM, dayName } from "@/lib/api";
import { PageHeader, StatCard, Spinner, EmptyState } from "@/components/common";
import { pct } from "@/pages/teacher/KpiPages";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Lightbulb, Users, Building2, Music2, RefreshCcw, Check, X, AlertTriangle,
  Info, Sparkles, TrendingUp,
} from "lucide-react";

const SEVERITY_STYLES = {
  important: "bg-[#FFEDEC] text-[#D90429]",
  suggested: "bg-[#FFF8DC] text-[#B8860B]",
  info: "bg-[#E7F0FE] text-[#1D4ED8]",
};
const STATUS_STYLES = {
  open: "bg-[#FFF8DC] text-[#B8860B]",
  accepted: "bg-[#D8F3DC] text-[#2D6A4F]",
  dismissed: "bg-[#FFEDEC] text-[#D90429]",
  superseded: "bg-muted text-muted-foreground",
};
const TYPE_LABELS = {
  hire_teacher: "Recruitment",
  assign_existing_teacher: "Demand routing",
  studio_expansion: "Studio expansion",
  studio_underutilized: "Studio utilization",
  teacher_underutilized: "Teacher capacity",
  teacher_overloaded: "Teacher overload",
};

function Chip({ value, styles, testId }) {
  return (
    <span data-testid={testId}
      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${styles[value] || "bg-muted text-muted-foreground"}`}>
      {String(value).replace(/_/g, " ")}
    </span>
  );
}

function UtilBar({ value }) {
  const v = Math.min(100, Math.round((value || 0) * 100));
  const color = v >= 90 ? "bg-[#D90429]" : v >= 75 ? "bg-[#B8860B]" : v < 40 ? "bg-muted-foreground/40" : "bg-[#2D6A4F]";
  return (
    <div className="h-2 rounded-full bg-muted w-full overflow-hidden">
      <div className={`h-full rounded-full ${color}`} style={{ width: `${v}%`, transition: "width 0.5s ease" }} />
    </div>
  );
}

function RecommendationCard({ rec, onAction }) {
  return (
    <div className="rounded-lg border bg-card p-5 lift" data-testid={`recommendation-${rec.id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Chip value={rec.severity} styles={SEVERITY_STYLES} />
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
          {TYPE_LABELS[rec.recommendation_type] || rec.recommendation_type}
        </span>
        <Chip value={rec.status} styles={STATUS_STYLES} testId={`rec-status-${rec.id}`} />
        <span className="text-xs text-muted-foreground ml-auto">{rec.generated_on}</span>
      </div>
      <p className="font-semibold">{rec.title}</p>
      <p className="text-sm text-muted-foreground mt-1">{rec.detail}</p>
      {(rec.rationale || []).length > 0 && (
        <ul className="mt-3 space-y-1.5">
          {rec.rationale.map((r, i) => (
            <li key={i} className="text-xs flex items-start gap-2">
              <span className="h-1 w-1 rounded-full bg-secondary mt-1.5 shrink-0" />{r}
            </li>
          ))}
        </ul>
      )}
      {rec.status === "open" && (
        <div className="flex gap-2 mt-4">
          <Button size="sm" onClick={() => onAction(rec.id, "accepted")} data-testid={`accept-rec-${rec.id}`}>
            <Check className="h-3.5 w-3.5 mr-1" />Accept
          </Button>
          <Button size="sm" variant="outline" onClick={() => onAction(rec.id, "dismissed")} data-testid={`dismiss-rec-${rec.id}`}>
            <X className="h-3.5 w-3.5 mr-1" />Dismiss
          </Button>
        </div>
      )}
    </div>
  );
}

function SuggestionsTab() {
  const [filter, setFilter] = useState("open");
  const { data: recs, loading, reload } = useList(`/admin/recommendations${filter ? `?status=${filter}` : ""}`, [filter]);
  const [generating, setGenerating] = useState(false);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post("/admin/recommendations/generate");
      toast.success(`${r.data.generated} recommendation(s) generated`);
      setFilter("open");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setGenerating(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.put(`/admin/recommendations/${id}/status`, { status });
      toast.success(`Recommendation ${status}`);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-5">
        {["open", "accepted", "dismissed", ""].map((f) => (
          <Button key={f || "all"} size="sm" variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)} data-testid={`filter-${f || "all"}`}>
            {f ? f.charAt(0).toUpperCase() + f.slice(1) : "All"}
          </Button>
        ))}
        <Button size="sm" className="ml-auto" onClick={generate} disabled={generating} data-testid="generate-recommendations-button">
          <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${generating ? "animate-spin" : ""}`} />
          {generating ? "Analysing..." : "Generate recommendations"}
        </Button>
      </div>
      {loading ? <Spinner full /> : !recs || recs.length === 0 ? (
        <EmptyState text='No recommendations here. Click "Generate recommendations" to analyse current demand and capacity.' />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger-in">
          {recs.map((r) => <RecommendationCard key={r.id} rec={r} onAction={setStatus} />)}
        </div>
      )}
    </div>
  );
}

function TeachersTab({ teachers }) {
  if (!teachers?.length) return <EmptyState text="No active teachers." />;
  return (
    <div className="space-y-3 stagger-in" data-testid="occupancy-list">
      {teachers.map((t) => (
        <div key={t.teacher_id} className="rounded-lg border bg-card p-5" data-testid={`occupancy-${t.teacher_id}`}>
          <div className="flex flex-wrap items-center gap-3 mb-3">
            <p className="font-semibold text-sm flex-1">{t.teacher_name}</p>
            <span className="text-xs text-muted-foreground">{t.courses.join(", ") || "No courses"}</span>
            <Chip value={t.status} styles={{
              critical: "bg-[#FFEDEC] text-[#D90429]", near_full: "bg-[#FFF8DC] text-[#B8860B]",
              healthy: "bg-[#D8F3DC] text-[#2D6A4F]", underutilized: "bg-[#E7F0FE] text-[#1D4ED8]",
              unknown: "bg-muted text-muted-foreground",
            }} />
          </div>
          <UtilBar value={t.occupancy} />
          <p className="text-xs text-muted-foreground mt-2">
            {t.occupancy == null ? (t.note || "Occupancy unknown")
              : `${pct(t.occupancy)} occupied — ${t.teaching_hours_per_week}h teaching of ${t.available_hours_per_week}h available · ${t.active_classes} weekly classes`}
          </p>
        </div>
      ))}
    </div>
  );
}

function StudiosTab({ studios }) {
  if (!studios?.length) return <EmptyState text="No active studios." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-in" data-testid="studio-utilization-list">
      {studios.map((s) => (
        <div key={s.studio_id} className="rounded-lg border bg-card p-5" data-testid={`studio-util-${s.studio_id}`}>
          <div className="flex items-center gap-3 mb-3">
            <p className="font-semibold text-sm flex-1">{s.studio_name}</p>
            <Chip value={s.status} styles={{
              high: "bg-[#FFEDEC] text-[#D90429]", moderate: "bg-[#FFF8DC] text-[#B8860B]",
              healthy: "bg-[#D8F3DC] text-[#2D6A4F]", low: "bg-[#E7F0FE] text-[#1D4ED8]",
            }} />
          </div>
          <UtilBar value={s.utilization} />
          <p className="text-xs text-muted-foreground mt-2">
            {pct(s.utilization)} of {s.open_hours_per_week}h weekly window · {s.classes} classes
            {s.busiest_day != null && ` · busiest: ${dayName(s.busiest_day)}`}
          </p>
        </div>
      ))}
    </div>
  );
}

function DemandTab({ courses }) {
  if (!courses?.length) return <EmptyState text="No active courses." />;
  return (
    <div className="space-y-3 stagger-in" data-testid="course-demand-list">
      {courses.map((c, i) => (
        <div key={c.course_id} className="rounded-lg border bg-card p-5 flex flex-wrap items-center gap-4" data-testid={`demand-${c.course_id}`}>
          <span className={`w-8 text-center font-display font-bold ${i === 0 ? "text-secondary" : "text-muted-foreground"}`}>#{i + 1}</span>
          <div className="flex-1 min-w-[140px]">
            <p className="font-semibold text-sm">{c.course_name}</p>
            <p className="text-xs text-muted-foreground">Last {c.window_days} days</p>
          </div>
          <div className="text-right"><p className="text-xs text-muted-foreground uppercase tracking-wider">Score</p>
            <p className="font-bold font-display">{c.demand_score}</p></div>
          <div className="text-right hidden sm:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Enquiries</p>
            <p className="font-bold font-display">{c.enquiries}+{c.trial_bookings}t</p></div>
          <div className="text-right hidden md:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Conversion</p>
            <p className="font-bold font-display">{pct(c.conversion_rate)}</p></div>
          <div className="text-right hidden md:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">New enrolments</p>
            <p className="font-bold font-display">{c.new_enrolments}</p></div>
          <div className="text-right hidden lg:block"><p className="text-xs text-muted-foreground uppercase tracking-wider">Open demand</p>
            <p className="font-bold font-display">{c.open_demand}</p></div>
          <div className="text-right"><p className="text-xs text-muted-foreground uppercase tracking-wider">Potential / month</p>
            <p className="font-bold font-display font-mono text-sm">{fmtRM(c.expected_monthly_revenue)}</p></div>
        </div>
      ))}
    </div>
  );
}

export default function RecommendationsPage() {
  const { data: overview, loading } = useList("/admin/recommendations/overview");
  return (
    <div data-testid="recommendations-page">
      <PageHeader title="Recommendations"
        description="Business intelligence from live demand, teacher capacity and studio utilization. Recruitment advice is guarded — occupancy alone never triggers it." />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 stagger-in mb-8">
        <StatCard icon={Users} label="Teachers tracked" value={overview?.teachers?.length ?? "—"}
          sub={`${(overview?.teachers || []).filter((t) => t.status === "underutilized").length} with spare capacity`} testId="rec-stat-teachers" />
        <StatCard icon={Building2} label="Studios tracked" value={overview?.studios?.length ?? "—"}
          sub={`${(overview?.studios || []).filter((s) => s.status === "high").length} near capacity`} testId="rec-stat-studios" />
        <StatCard icon={TrendingUp} label="Top demand" value={overview?.courses?.[0]?.course_name ?? "—"}
          sub={overview?.courses?.[0] ? `Score ${overview.courses[0].demand_score}` : ""} accent testId="rec-stat-demand" />
      </div>
      <Tabs defaultValue="suggestions">
        <TabsList className="mb-5">
          <TabsTrigger value="suggestions" data-testid="tab-suggestions"><Lightbulb className="h-3.5 w-3.5 mr-1.5" />Suggestions</TabsTrigger>
          <TabsTrigger value="teachers" data-testid="tab-teachers"><Users className="h-3.5 w-3.5 mr-1.5" />Teacher capacity</TabsTrigger>
          <TabsTrigger value="studios" data-testid="tab-studios"><Building2 className="h-3.5 w-3.5 mr-1.5" />Studios</TabsTrigger>
          <TabsTrigger value="demand" data-testid="tab-demand"><Music2 className="h-3.5 w-3.5 mr-1.5" />Course demand</TabsTrigger>
        </TabsList>
        <TabsContent value="suggestions"><SuggestionsTab /></TabsContent>
        <TabsContent value="teachers">{loading ? <Spinner full /> : <TeachersTab teachers={overview?.teachers} />}</TabsContent>
        <TabsContent value="studios">{loading ? <Spinner full /> : <StudiosTab studios={overview?.studios} />}</TabsContent>
        <TabsContent value="demand">{loading ? <Spinner full /> : <DemandTab courses={overview?.courses} />}</TabsContent>
      </Tabs>
    </div>
  );
}
